import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  Stack,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
  Fade,
  Grow,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Backdrop,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { styled, useTheme, alpha, keyframes } from '@mui/material/styles';
import {
  Search as SearchIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Launch as LaunchIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  MonetizationOn as MoneyIcon,
  Wallet as WalletIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Star as StarIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorOutlineIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import client from '../../../API';
import routes from '../../../Config/routes';
import useAuth from '../../../Hooks/use-auth';

// Import the balance ledger modal from BlitzPay
import BalanceLedgerModal from '../../BlitzPay/blitzpaypagecomponents/BlitzPayBalanceDialog';

// ✨ Import wallet and tax management hooks ✨
import { useWalletManagement } from '../../Dashboard/Users/wallets/useWalletManagement';
import { useTaxManagement } from '../../Dashboard/Users/wallets/useTaxManagement';
// ========== ANIMATIONS ==========
const gentlePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.02); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ========== STYLED COMPONENTS ==========
const HeaderCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  color: theme.palette.success.contrastText,
  borderRadius: 20,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.25)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at top right, ${alpha('#fff', 0.1)} 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
}));

const MetricCard = styled(Box)(({ theme, isPrimary }) => ({
  background: isPrimary
    ? `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.15)} 100%)`
    : `linear-gradient(135deg, ${alpha('#fff', 0.12)} 0%, ${alpha('#fff', 0.08)} 100%)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  border: isPrimary ? `2px solid ${alpha('#fff', 0.3)}` : `1px solid ${alpha('#fff', 0.15)}`,
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: isPrimary ? 'pointer' : 'default',
  '&:hover': {
    background: isPrimary
      ? `linear-gradient(135deg, ${alpha('#fff', 0.25)} 0%, ${alpha('#fff', 0.2)} 100%)`
      : `linear-gradient(135deg, ${alpha('#fff', 0.18)} 0%, ${alpha('#fff', 0.12)} 100%)`,
    transform: isPrimary ? 'translateY(-2px)' : 'none',
    boxShadow: isPrimary ? `0 4px 16px ${alpha('#000', 0.15)}` : 'none',
  },
}));

const EnhancedCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.success.main,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  boxShadow: theme.shadows[2],
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusColors = {
    pending: { bg: theme.palette.warning.main, color: theme.palette.warning.contrastText },
    approved: { bg: theme.palette.info.main, color: theme.palette.info.contrastText },
    paid: { bg: theme.palette.success.main, color: theme.palette.success.contrastText },
    funded: { bg: theme.palette.success.dark, color: theme.palette.success.contrastText },
    cancelled: { bg: theme.palette.error.main, color: theme.palette.error.contrastText },
    claimable: { bg: theme.palette.success.light, color: theme.palette.success.contrastText },
  };
  
  const config = statusColors[status] || { bg: theme.palette.grey[400], color: theme.palette.grey[900] };
  
  return {
    backgroundColor: config.bg,
    color: config.color,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'capitalize',
    borderRadius: 6,
  };
});

const ClaimableCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `2px solid ${theme.palette.success.main}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.light, 0.02)} 100%)`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${gentlePulse} 3s ease-in-out infinite`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.3)}`,
    animation: 'none',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
  },
}));

const PaymentMethodsCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  marginBottom: theme.spacing(3),
  border: '2px solid',
  borderColor: theme.palette.divider,
  background: theme.palette.background.paper,
  overflow: 'hidden',
}));

// ========== WALLET ICON COMPONENT ==========
const WalletTypeIcon = ({ type }) => {
  const iconMap = {
    bank: <AccountBalanceIcon />,
    paypal: <AccountBalanceWalletIcon />,
    stripe: <CreditCardIcon />,
    wise: <BusinessIcon />,
    crypto: <MoneyIcon />,
  };
  return iconMap[type] || <WalletIcon />;
};

// ========== MAIN COMPONENT ==========
export default function AgencyMoneySimplified() {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  
  const [activeTab, setActiveTab] = useState('claimable');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, payout: null });
  const [claimingPayoutId, setClaimingPayoutId] = useState(null);
  const [expandedDiagnostics, setExpandedDiagnostics] = useState({});
  const [balanceLedgerOpen, setBalanceLedgerOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ✨ Snackbar helper for hooks ✨
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ✨ USE THE WALLET AND TAX MANAGEMENT HOOKS ✨
  const {
    wallets,
    walletsLoading,
    fetchWallets,
  } = useWalletManagement(showSnackbar);

  const {
    taxInfo,
    taxInfoLoading,
    fetchTaxInfo,
  } = useTaxManagement(showSnackbar);

  // ========== QUERIES ==========
  const { data: payoutsData, isLoading: payoutsLoading, error: payoutsError, refetch: refetchPayouts } = useQuery(
    'agencyAllPayouts',
    client.agency.payouts,
    { refetchOnWindowFocus: false }
  );

  const { data: claimableData, isLoading: claimableLoading, error: claimableError, refetch: refetchClaimable } = useQuery(
    'agencyClaimablePayouts',
    client.agency.getClaimableInvoices,
    { refetchOnWindowFocus: false }
  );

  const { data: balanceData, isLoading: balanceLoading, refetch: refetchBalance } = useQuery(
    'agencyBalance',
    () => client.balanceLedger.fetchMyBalance(),
    { refetchInterval: 30000 }
  );

  // Fetch balance ledger history for the modal
  const { data: ledgerHistoryData, isLoading: ledgerHistoryLoading, refetch: refetchLedger } = useQuery(
    ['agencyBalanceLedgerHistory', balanceData?.user?.id],
    () => client.balanceLedger.fetch(balanceData?.user?.id),
    { enabled: !!balanceData?.user?.id }
  );

  // ✨ FETCH WALLETS AND TAX INFO ON MOUNT ✨
  useEffect(() => {
    if (userInfo?.id) {
      console.log('🚀 Fetching wallets and tax info for user:', userInfo.id);
      fetchWallets();
      fetchTaxInfo();
    }
  }, [userInfo?.id, fetchWallets, fetchTaxInfo]);

  // ========== AGGRESSIVE REFRESH FUNCTION ==========
  const performFullRefresh = useCallback(async () => {
    console.log('🔄 Starting full data refresh...');
    setIsRefreshing(true);
    
    try {
      // Refetch all queries in parallel
      await Promise.all([
        refetchPayouts(),
        refetchClaimable(),
        refetchBalance(),
        refetchLedger(),
        fetchWallets(),
        fetchTaxInfo(),
      ]);
      
      console.log('✅ Full refresh completed successfully');
    } catch (error) {
      console.error('❌ Error during refresh:', error);
      setSnackbar({
        open: true,
        message: 'Failed to refresh data. Please reload the page.',
        severity: 'warning'
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchPayouts, refetchClaimable, refetchBalance, refetchLedger, fetchWallets, fetchTaxInfo]);

  // ========== MUTATIONS ==========
  const claimMutation = useMutation(
    (invoiceId) => {
      console.log('🚀 Claiming invoice ID:', invoiceId);
      return client.agency.claimInvoiceForCreator(invoiceId);
    },
    {
      onMutate: (invoiceId) => {
        console.log('⏳ Mutation started for invoice:', invoiceId);
        setClaimingPayoutId(invoiceId);
      },
      onSuccess: async (response) => {
        console.log('✅ Claim successful:', response);
        
        // Show success message immediately
        setSnackbar({
          open: true,
          message: response.message || `✅ Claimed ${response.amount_formatted}! New balance: ${response.new_balance_formatted}`,
          severity: 'success'
        });
        
        // Close the dialog
        setConfirmDialog({ open: false, payout: null });
        
        // ✨ AGGRESSIVE REFRESH - Multiple strategies for maximum reliability ✨
        console.log('🔄 Triggering aggressive data refresh...');
        
        // Strategy 1: Invalidate all queries (tells react-query they're stale)
        queryClient.invalidateQueries('agencyAllPayouts');
        queryClient.invalidateQueries('agencyClaimablePayouts');
        queryClient.invalidateQueries('agencyBalance');
        queryClient.invalidateQueries(['agencyBalanceLedgerHistory']);
        
        // Strategy 2: Force immediate refetch (don't wait for next render)
        await performFullRefresh();
        
        console.log('✅ All refresh strategies completed');
      },
      onError: (error) => {
        console.error('❌ Claim failed:', error);
        console.error('Error response:', error?.response);
        setSnackbar({
          open: true,
          message: error?.response?.data?.error || 'Failed to claim payment',
          severity: 'error'
        });
      },
      onSettled: () => {
        console.log('🏁 Mutation settled');
        setClaimingPayoutId(null);
      }
    }
  );

  // ========== DATA PROCESSING ==========
  const payouts = payoutsData?.payouts || [];
  const claimablePayouts = claimableData?.claimable_payouts || [];
  const agencyBalance = balanceData?.balance ? parseFloat(balanceData.balance) : 0;
  const ledgerEntries = balanceData?.ledger_entries || [];
  const transitBalance = balanceData?.transit_balance || 0;
  
  // ✨ Get primary wallet from the wallets array ✨
  const primaryWallet = wallets.find(w => w.is_primary) || wallets[0];

  // Calculate statistics
  const statistics = useMemo(() => {
    const activePayouts = payouts.filter(p => p.status !== 'cancelled');
    
    return {
      total: activePayouts.length,
      claimable: claimablePayouts.length,
      claimableAmount: claimablePayouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      pending: activePayouts.filter(p => p.status === 'pending').length,
      pendingAmount: activePayouts
        .filter(p => p.status === 'pending')
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      completed: activePayouts.filter(p => ['paid', 'funded'].includes(p.status)).length,
      completedAmount: activePayouts
        .filter(p => ['paid', 'funded'].includes(p.status))
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      totalAmount: activePayouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    };
  }, [payouts, claimablePayouts]);

  // Helper: Check if payout is claimable
  const isClaimable = useCallback((payout) => {
    return claimablePayouts.some(cp => cp.id === payout.id);
  }, [claimablePayouts]);

  // Helper: Get diagnostic info for non-claimable payouts
  const getDiagnostics = useCallback((payout) => {
    const diagnostics = {
      reasons: [],
      canFix: false,
      helpText: '',
      severity: 'info',
    };

    if (isClaimable(payout)) {
      return { ...diagnostics, reasons: ['✓ Ready to claim!'], severity: 'success' };
    }

    if (payout.status !== 'pending') {
      diagnostics.reasons.push(`Status: ${payout.status} (must be "pending")`);
      diagnostics.helpText = 'Payout must be in pending status to claim.';
    }

    if (payout.withdrawal_id) {
      if (payout.metadata?.claimed_by_agency) {
        diagnostics.reasons.push('Already claimed by your agency');
        diagnostics.severity = 'success';
        diagnostics.helpText = `Claimed on ${new Date(payout.metadata.claimed_by_agency.claimed_at).toLocaleDateString()}`;
      } else {
        diagnostics.reasons.push('Withdrawn by creator');
        diagnostics.severity = 'warning';
        diagnostics.helpText = 'Creator has already withdrawn these funds.';
      }
    } else {
      diagnostics.reasons.push('No paid invoice found');
      diagnostics.canFix = true;
      diagnostics.severity = 'warning';
      diagnostics.helpText = 'Waiting for client to pay the invoice. Once invoice is marked "paid" and campaign is "Completed", you can claim.';
    }

    return diagnostics;
  }, [isClaimable]);

  // Filter payouts based on active tab
  const filteredPayouts = useMemo(() => {
    let filtered = payouts;

    switch (activeTab) {
      case 'claimable':
        filtered = payouts.filter(p => isClaimable(p));
        break;
      case 'pending':
        filtered = payouts.filter(p => p.status === 'pending' && !isClaimable(p));
        break;
      case 'completed':
        filtered = payouts.filter(p => ['paid', 'funded'].includes(p.status));
        break;
      case 'all':
      default:
        filtered = payouts.filter(p => p.status !== 'cancelled');
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.po_number?.toLowerCase().includes(query) ||
        p.creator_id?.toLowerCase().includes(query) ||
        p.campaign_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [payouts, activeTab, searchQuery, isClaimable]);

  // ========== HANDLERS ==========
  const handleClaimClick = useCallback((payout) => {
    console.log('👆 Claim clicked for payout:', payout);
    
    // Find the matching claimable payout to get invoice_id
    const claimablePayout = claimablePayouts.find(cp => cp.id === payout.id);
    
    if (claimablePayout && claimablePayout.invoice_id) {
      console.log('✅ Found invoice_id:', claimablePayout.invoice_id);
      setConfirmDialog({ 
        open: true, 
        payout: {
          ...payout,
          invoice_id: claimablePayout.invoice_id,
          invoice_po_number: claimablePayout.invoice_po_number
        }
      });
    } else {
      console.error('❌ No invoice_id found for payout:', payout.id);
      setSnackbar({
        open: true,
        message: 'Cannot claim: Invoice ID not found. Please refresh the page.',
        severity: 'error'
      });
    }
  }, [claimablePayouts]);

  const handleConfirmClaim = useCallback(() => {
    console.log('🎯 Confirm claim clicked');
    console.log('Dialog state:', confirmDialog);
    
    const invoiceId = confirmDialog.payout?.invoice_id;
    
    if (invoiceId) {
      console.log('✅ Invoking mutation with invoice_id:', invoiceId);
      claimMutation.mutate(invoiceId);
    } else {
      console.error('❌ No invoice_id in confirm dialog!');
      console.error('Payout object:', confirmDialog.payout);
      setSnackbar({
        open: true,
        message: 'Cannot claim: Invoice ID missing. Please try again.',
        severity: 'error'
      });
    }
  }, [confirmDialog, claimMutation]);

  const toggleDiagnostics = useCallback((payoutId) => {
    setExpandedDiagnostics(prev => ({
      ...prev,
      [payoutId]: !prev[payoutId]
    }));
  }, []);

  // Manual refresh handler for the refresh button
  const handleManualRefresh = useCallback(async () => {
    console.log('🔄 Manual refresh triggered');
    await performFullRefresh();
    setSnackbar({
      open: true,
      message: '✅ Data refreshed successfully',
      severity: 'success'
    });
  }, [performFullRefresh]);

  // Navigate to settings
  const handleNavigateToSettings = useCallback(() => {
    navigate('/user?tab=payments');
  }, [navigate]);

  // ========== PAYMENT METHODS STATUS CARD ==========
  const renderPaymentMethodsCard = () => {
    const hasWallets = wallets.length > 0;
    const hasTaxInfo = !!taxInfo;
    const isFullyConfigured = hasWallets && hasTaxInfo;
    
    console.log('💳 Payment Methods Card:', {
      walletsCount: wallets.length,
      walletsLoading,
      hasTaxInfo,
      taxInfoLoading,
      primaryWallet,
      taxInfo
    });
    
    // Show loading state if either is loading
    if (walletsLoading || taxInfoLoading) {
      return (
        <PaymentMethodsCard elevation={3}>
          <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading payment configuration...
              </Typography>
            </Box>
          </CardContent>
        </PaymentMethodsCard>
      );
    }
    
    return (
      <PaymentMethodsCard elevation={3}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: isFullyConfigured ? theme.palette.success.main : theme.palette.warning.main,
                  width: 48,
                  height: 48
                }}
              >
                {isFullyConfigured ? <CheckCircleIcon /> : <WarningIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Payment Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isFullyConfigured 
                    ? 'Your payment methods are configured and ready'
                    : 'Complete your payment setup to receive funds'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              endIcon={<NavigateNextIcon />}
              onClick={handleNavigateToSettings}
              sx={{ minWidth: 150 }}
            >
              Manage Settings
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {/* Payment Methods Section */}
            <Grid item xs={12} md={6}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccountBalanceWalletIcon color={hasWallets ? 'success' : 'disabled'} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Payment Methods
                  </Typography>
                  {hasWallets && (
                    <Chip
                      label={`${wallets.length} linked`}
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>

                {hasWallets ? (
                  <Box>
                    {primaryWallet && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 1.5,
                          border: '2px solid',
                          borderColor: 'success.main',
                          bgcolor: alpha(theme.palette.success.main, 0.05)
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <WalletTypeIcon type={primaryWallet.wallet_type} />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {primaryWallet.nickname || 'Primary Method'}
                              </Typography>
                              <Chip
                                icon={<StarIcon />}
                                label="Preferred"
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {primaryWallet.wallet_type === 'bank' && primaryWallet.bank_name && `${primaryWallet.bank_name} ${primaryWallet.bank_account_number || ''}`}
                              {primaryWallet.wallet_type === 'paypal' && primaryWallet.paypal_email}
                              {primaryWallet.wallet_type === 'stripe' && 'Stripe Connect'}
                              {primaryWallet.wallet_type === 'wise' && primaryWallet.wise_email}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}
                    
                    {wallets.length > 1 && (
                      <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                        You have {wallets.length - 1} other payment method{wallets.length > 2 ? 's' : ''} configured
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Alert severity="warning" icon={<ErrorOutlineIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      No payment methods configured
                    </Typography>
                    <Typography variant="caption">
                      Add a payment method to receive your agency commissions
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Grid>

            {/* Tax Information Section */}
            <Grid item xs={12} md={6}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ReceiptIcon color={hasTaxInfo ? 'success' : 'disabled'} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Tax Information
                  </Typography>
                  {hasTaxInfo && (
                    <Chip
                      label="Verified"
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>

                {hasTaxInfo ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <List dense disablePadding>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Tax ID Type"
                          secondary={taxInfo.tax_id_type?.toUpperCase() || 'N/A'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Business Name"
                          secondary={taxInfo.business_name || taxInfo.legal_name || 'N/A'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="W9 Status"
                          secondary={taxInfo.w9_submitted ? 'Submitted' : 'Pending'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                    </List>
                  </Paper>
                ) : (
                  <Alert severity="warning" icon={<ErrorOutlineIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Tax information not provided
                    </Typography>
                    <Typography variant="caption">
                      Required for compliance and payment processing
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Action Banner */}
          {!isFullyConfigured && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity="warning"
                icon={<InfoIcon />}
                action={
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<SettingsIcon />}
                    onClick={handleNavigateToSettings}
                  >
                    Complete Setup
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Action Required: {!hasWallets && 'Add payment method'}{!hasWallets && !hasTaxInfo && ' and '}{!hasTaxInfo && 'complete tax information'}
                </Typography>
              </Alert>
            </Box>
          )}
        </CardContent>
      </PaymentMethodsCard>
    );
  };

  // ========== RENDER HELPERS ==========
  const renderPayoutCard = (payout) => {
    const claimable = isClaimable(payout);
    const diagnostics = getDiagnostics(payout);
    const CardComponent = claimable ? ClaimableCard : EnhancedCard;

    return (
      <CardComponent key={payout.id}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<ReceiptIcon />}
              label="Payout"
              size="small"
              color="success"
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
            <StatusChip
              status={claimable ? 'claimable' : payout.status}
              label={claimable ? 'Ready' : payout.status}
              size="small"
            />
            {payout.metadata?.claimed_by_agency && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Claimed"
                size="small"
                color="info"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              />
            )}
          </Box>

          {/* PO Number */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '1.1rem' }}>
            {payout.po_number || `Payout #${payout.id}`}
          </Typography>

          {/* Details */}
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
              <Typography variant="body2" noWrap>{payout.creator_id}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AttachMoneyIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                ${parseFloat(payout.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            {payout.campaign_name && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', color: 'text.secondary' }}>
                <CampaignIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, mt: 0.5 }} />
                <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                  {payout.campaign_name}
                </Typography>
              </Box>
            )}

            {payout.payout_date && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <ScheduleIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                <Typography variant="caption">
                  {new Date(payout.payout_date).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Diagnostics for non-claimable */}
          {!claimable && activeTab === 'pending' && (
            <Box sx={{ mt: 2 }}>
              <Accordion
                expanded={expandedDiagnostics[payout.id] || false}
                onChange={() => toggleDiagnostics(payout.id)}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                    <Typography variant="body2" fontWeight={600}>
                      Why can't I claim this?
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {diagnostics.reasons.map((reason, idx) => (
                      <Typography key={idx} variant="caption" color="text.secondary">
                        • {reason}
                      </Typography>
                    ))}
                    {diagnostics.helpText && (
                      <Alert severity={diagnostics.severity} sx={{ mt: 1, fontSize: '0.75rem' }}>
                        {diagnostics.helpText}
                      </Alert>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* Claimed info */}
          {payout.metadata?.claimed_by_agency && (
            <Alert severity="success" sx={{ mt: 2, py: 0.5, fontSize: '0.75rem' }}>
              Claimed on {new Date(payout.metadata.claimed_by_agency.claimed_at).toLocaleDateString()}
            </Alert>
          )}
        </CardContent>

        <CardActions
          sx={{
            justifyContent: 'space-between',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            px: 2,
            py: 1.5,
            background: alpha(theme.palette.background.default, 0.3),
          }}
        >
          {claimable ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={claimingPayoutId === payout.invoice_id ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={() => handleClaimClick(payout)}
              disabled={claimingPayoutId === payout.invoice_id}
              sx={{ fontWeight: 600, flex: 1 }}
            >
              {claimingPayoutId === payout.invoice_id ? 'Claiming...' : 'Claim to Balance'}
            </Button>
          ) : payout.has_creator_user ? (
            <Button
              size="small"
              endIcon={<LaunchIcon />}
              onClick={() => navigate(routes.payoutSpec.replace(':payoutId', payout.id))}
              sx={{ fontWeight: 600 }}
            >
              View Details
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Waiting for conditions
            </Typography>
          )}
        </CardActions>
      </CardComponent>
    );
  };

  // ========== LOADING & ERROR STATES ==========
  if (payoutsLoading || claimableLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (payoutsError || claimableError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          Failed to load data. Please refresh the page.
        </Alert>
      </Container>
    );
  }

  // ========== MAIN RENDER ==========
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isRefreshing}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Refreshing data...
          </Typography>
        </Box>
      </Backdrop>

      {/* Header */}
      <HeaderCard elevation={0}>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ bgcolor: alpha('#fff', 0.25), width: 56, height: 56, boxShadow: `0 4px 12px ${alpha('#000', 0.15)}` }}>
                <BusinessIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight="bold">Agency Payments</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Manage creator earnings & commissions</Typography>
              </Box>
              {/* Refresh Button */}
              <Tooltip title="Refresh all data">
                <IconButton
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    '&:hover': { bgcolor: alpha('#fff', 0.3) },
                    color: 'white',
                  }}
                >
                  <RefreshIcon sx={{ 
                    animation: isRefreshing ? `${spin} 1s linear infinite` : 'none' 
                  }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MetricCard isPrimary onClick={() => setBalanceLedgerOpen(true)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WalletIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.9 }}>Your Blitz Balance</Typography>
                    <Tooltip title="Click to view transaction history" arrow placement="top">
                      <InfoIcon sx={{ fontSize: '1rem', opacity: 0.7, cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {balanceLoading ? <CircularProgress size={24} sx={{ color: alpha('#fff', 0.7) }} /> : `$${agencyBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Available funds • Click to view history</Typography>
                </MetricCard>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MetricCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MoneyIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.9 }}>Claimable Now</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    ${statistics.claimableAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {statistics.claimable} payouts ready
                  </Typography>
                </MetricCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Payment Methods Configuration Card */}
      {renderPaymentMethodsCard()}

      {/* Info Banner for Claimable */}
      {statistics.claimable > 0 && (
        <Fade in>
          <Alert
            severity="success"
            icon={<TrendingUpIcon />}
            sx={{ mb: 3, borderRadius: 3, fontWeight: 600 }}
            action={
              <Button color="inherit" size="small" onClick={() => setActiveTab('claimable')}>
                View All
              </Button>
            }
          >
            💰 {statistics.claimable} payouts (${statistics.claimableAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}) ready to claim!
          </Alert>
        </Fade>
      )}

      {/* Search & Filters */}
      <EnhancedCard sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by PO number, creator, or campaign..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Grid view">
                <IconButton
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  onClick={() => setViewMode('grid')}
                  sx={{ border: 1, borderColor: viewMode === 'grid' ? 'primary.main' : 'divider', borderRadius: 2 }}
                >
                  <ViewModuleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  onClick={() => setViewMode('list')}
                  sx={{ border: 1, borderColor: viewMode === 'list' ? 'primary.main' : 'divider', borderRadius: 2 }}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </EnhancedCard>

      {/* Tabs */}
      <EnhancedCard sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              py: 2.5,
              '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.05) }
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={statistics.claimable} color="success" max={99}>
                  <MoneyIcon fontSize="small" />
                </Badge>
                Claimable
              </Box>
            }
            value="claimable"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PendingIcon fontSize="small" />
                Pending ({statistics.pending - statistics.claimable})
              </Box>
            }
            value="pending"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon fontSize="small" />
                Completed ({statistics.completed})
              </Box>
            }
            value="completed"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon fontSize="small" />
                All ({statistics.total})
              </Box>
            }
            value="all"
          />
        </Tabs>
      </EnhancedCard>

      {/* Tab-specific info */}
      {activeTab === 'pending' && statistics.pending - statistics.claimable > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Why are these payouts pending?
          </Typography>
          <Typography variant="caption">
            Click "Why can't I claim this?" on each card to see specific reasons. Common issues: invoice not paid yet, campaign not completed, or already withdrawn.
          </Typography>
        </Alert>
      )}

      {/* Content */}
      {filteredPayouts.length === 0 ? (
        <EnhancedCard sx={{ p: 6, textAlign: 'center' }}>
          <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No payouts found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'claimable' ? 'Check back soon for claimable payouts!' : 'Try adjusting your filters'}
          </Typography>
        </EnhancedCard>
      ) : (
        <Fade in timeout={800}>
          <Grid container spacing={3}>
            {filteredPayouts.map((payout, index) => (
              <Grow in timeout={800 + index * 100} key={payout.id}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {renderPayoutCard(payout)}
                </Grid>
              </Grow>
            ))}
          </Grid>
        </Fade>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, payout: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DownloadIcon color="success" />
            Claim Payment to Agency Balance
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Claim this payment on behalf of <strong>{confirmDialog.payout?.creator_id}</strong>?
            </Typography>

            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                What happens:
              </Typography>
              <Stack spacing={1} sx={{ pl: 2 }}>
                <Typography variant="body2">
                  • Amount: <strong>${confirmDialog.payout?.amount}</strong>
                </Typography>
                <Typography variant="body2">
                  • Credited to your agency balance instantly
                </Typography>
                <Typography variant="body2">
                  • <strong>No fees deducted</strong>
                </Typography>
                <Typography variant="body2">
                  • Creator will NOT receive these funds
                </Typography>
              </Stack>
            </Paper>

            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                Funds are added to your balance immediately. You can withdraw to your bank account later from the balance page.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setConfirmDialog({ open: false, payout: null })} disabled={claimMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClaim}
            variant="contained"
            color="success"
            disabled={claimMutation.isLoading}
            startIcon={claimMutation.isLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {claimMutation.isLoading ? 'Claiming...' : 'Confirm Claim'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Balance Ledger Modal */}
      <BalanceLedgerModal
        open={balanceLedgerOpen}
        onClose={() => setBalanceLedgerOpen(false)}
        ledgerEntries={ledgerEntries}
        isLoading={ledgerHistoryLoading}
        isError={false}
        error={null}
        currentBalance={agencyBalance}
        transitBalance={transitBalance}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}